/**
 * AgentIcon — renders a registry icon name (react-icons/hi2) at any size.
 *
 * The registry stores icon names as strings so agent definitions stay
 * serializable; this is the single place they resolve to components. Used by
 * the AgentSelector grid (solid, large) and the Sidebar agents subnav
 * (outline, small — matching the rest of the nav rail).
 */

import React from 'react';
import type { IconType } from 'react-icons';
import {
  HiBell,
  HiChartBar,
  HiChatBubbleLeftRight,
  HiClock,
  HiCpuChip,
  HiExclamationCircle,
  HiEye,
  HiInboxStack,
  HiSignal,
  HiSquaresPlus,
  HiOutlineBell,
  HiOutlineChartBar,
  HiOutlineChatBubbleLeftRight,
  HiOutlineClock,
  HiOutlineCpuChip,
  HiOutlineExclamationCircle,
  HiOutlineEye,
  HiOutlineInboxStack,
  HiOutlineSignal,
  HiOutlineSquaresPlus
} from 'react-icons/hi2';

/** Registry icon name → { solid, outline } component pair */
const ICON_MAP: Record<string, { solid: IconType; outline: IconType }> = {
  HiBell: { solid: HiBell, outline: HiOutlineBell },
  HiChartBar: { solid: HiChartBar, outline: HiOutlineChartBar },
  HiChatBubbleLeftRight: { solid: HiChatBubbleLeftRight, outline: HiOutlineChatBubbleLeftRight },
  HiClock: { solid: HiClock, outline: HiOutlineClock },
  HiCpuChip: { solid: HiCpuChip, outline: HiOutlineCpuChip },
  HiExclamationCircle: { solid: HiExclamationCircle, outline: HiOutlineExclamationCircle },
  HiEye: { solid: HiEye, outline: HiOutlineEye },
  HiInboxStack: { solid: HiInboxStack, outline: HiOutlineInboxStack },
  HiSignal: { solid: HiSignal, outline: HiOutlineSignal },
  HiSquaresPlus: { solid: HiSquaresPlus, outline: HiOutlineSquaresPlus }
};

interface AgentIconProps {
  /** Icon name from the agent/feature registry (e.g. 'HiClock') */
  name: string;
  /** solid (filled) for the selector cards, outline for the nav rail */
  variant?: 'solid' | 'outline';
  className?: string;
}

export const AgentIcon: React.FC<AgentIconProps> = ({ name, variant = 'solid', className }) => {
  const pair = ICON_MAP[name] ?? ICON_MAP.HiSquaresPlus;
  const Icon = pair[variant];
  return <Icon className={className} aria-hidden="true" />;
};
